import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $createParagraphNode,
  $createTextNode,
  COMMAND_PRIORITY_HIGH,
  PASTE_COMMAND,
  type LexicalEditor,
  type LexicalNode,
  type ParagraphNode,
  type TextNode,
} from 'lexical';
import { parseComposerReferences } from '@runloop/reflex-contract';
import { splitContextUrls } from '@reflex/ui/client/context-url-matchers';
import { cn } from '@reflex/ui/lib/utils';
import { $createMentionNode, MentionNode, type MentionData } from './MentionNode';
import { resolveLinkedContextUrls } from './paste-links';

/**
 * Append a stored prompt string to a paragraph, re-hydrating any reference
 * markers (compact drafts) or expanded blocks (launch again) — agent-run and
 * plugin context alike — back into atomic chips. This is why draft persistence
 * + "launch again" get chips for free: the saved prompt string flows straight
 * through here.
 */
function $appendComposerContent(paragraph: ParagraphNode, text: string): void {
  for (const segment of parseComposerReferences(text)) {
    if (segment.type === 'text') {
      if (segment.value) paragraph.append($createTextNode(segment.value));
    } else {
      paragraph.append($createMentionNode(segmentToMention(segment)));
    }
  }
}

/** Map a non-text composer segment onto the matching mention chip data. */
function segmentToMention(
  segment: Exclude<ReturnType<typeof parseComposerReferences>[number], { type: 'text' }>,
): MentionData {
  return segment.type === 'reference'
    ? { kind: 'agent-run', id: segment.id, title: segment.title }
    : { kind: 'context', contextKind: segment.kind, id: segment.id, title: segment.title };
}

/** One piece of pasted text: a literal run, or a chip to insert for it. */
type PastePiece = { type: 'text'; value: string } | { type: 'mention'; mention: MentionData };

/**
 * Resolve pasted text into the pieces the editor should insert: reference
 * markers (a copied chip, a persisted draft) always become chips, and — when
 * `contextUrls` is on — so do item URLs a plugin claims (a pull request link
 * copied from GitHub or from the transcript). Everything else stays literal
 * text.
 *
 * Returns `null` when nothing in the paste is a reference, so the caller can
 * let Lexical's default paste handle it — preserving whatever the clipboard
 * carried beyond plain text.
 */
function pastePieces(text: string, contextUrls: boolean): PastePiece[] | null {
  const pieces: PastePiece[] = [];
  let sawReference = false;
  for (const segment of parseComposerReferences(text)) {
    if (segment.type !== 'text') {
      sawReference = true;
      pieces.push({ type: 'mention', mention: segmentToMention(segment) });
      continue;
    }
    const parts = contextUrls
      ? splitContextUrls(segment.value)
      : [{ type: 'text' as const, value: segment.value }];
    for (const part of parts) {
      if (part.type === 'text') {
        if (part.value) pieces.push({ type: 'text', value: part.value });
        continue;
      }
      sawReference = true;
      pieces.push({
        type: 'mention',
        mention: { kind: 'context', contextKind: part.kind, id: part.id, title: part.title },
      });
    }
  }
  return sawReference ? pieces : null;
}

/** An active `@`-query in the editor: the typed text and the caret rectangle. */
export interface MentionQuery {
  query: string;
  rect: DOMRect;
}

export interface MentionEditorHandle {
  /** Current serialized value: plain text with inline chips as their markers. */
  getText: () => string;
  /** Replace the whole content with `text` (markers are NOT re-parsed). */
  setText: (text: string) => void;
  /** Insert plain text at the caret (e.g. speech transcript). */
  insertText: (text: string) => void;
  /** Insert an inline chip at the caret (no active query needed). */
  insertMention: (mention: MentionData) => void;
  /** Replace the active `@`-query with an inline chip. */
  insertMentionAtQuery: (mention: MentionData) => void;
  /**
   * Insert an `@` at the caret and open the mention menu, exactly as if the
   * user had typed it. Used by the "+" trigger so the button and `@` share one
   * code path. When the caret already sits in an `@`-token, that token is
   * reused (nothing is inserted) instead of stacking a second `@`.
   *
   * Returns whether an `@` was inserted, so the caller knows whether a
   * dismissed menu should clean the character back up.
   */
  openMentionMenu: () => boolean;
  /** Remove the active `@`-query text (caret lands where the `@` was). */
  clearQuery: () => void;
  /** Clear the query text after `@` but keep the `@` and caret (menu stays open). */
  resetQuery: () => void;
  /**
   * Replace the query text after `@` with `text`, keeping the `@` and dropping
   * the caret at the end (menu stays open). Used to scope the menu to a category
   * by rewriting the token to `@<keyword>:`.
   */
  setQuery: (text: string) => void;
  clear: () => void;
  focus: () => void;
}

export interface MentionEditorProps {
  initialText?: string;
  placeholder?: ReactNode;
  className?: string;
  autoFocus?: boolean;
  editable?: boolean;
  /** Fires with the serialized value (text + chip markers) on every change. */
  onChange?: (text: string) => void;
  /** Reports the active `@`-query (or null) so the host can drive a menu. */
  onMentionQueryChange?: (active: MentionQuery | null) => void;
  /**
   * Capture-phase keydown on the editable. Return `true` to consume the key
   * (the host handled it — e.g. menu nav, submit); the editor then skips its
   * own handling. Runs before Lexical's listeners.
   */
  onKeyDownCapture?: (e: ReactKeyboardEvent<HTMLElement>) => boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLElement>) => void;
  /**
   * Turn a pasted item URL (a pull request link, ...) into the same context
   * chip its `@`-mention would insert. Off by default, and deliberately so: a
   * chip serializes to a marker that only reaches an item's real content on
   * surfaces whose backend expands it (the agent prompt and message paths).
   * On any other composer the marker would just be worse text than the URL the
   * user pasted.
   */
  contextUrls?: boolean;
}

const EDITOR_THEME = { paragraph: 'm-0' };

// Match a trailing `@query` at the caret: start-of-text or after whitespace.
// The query may contain spaces (multi-word titles), but never starts with one
// (so `me @ example.com` doesn't fire) and never contains a second `@`.
const MENTION_AT_CARET = /(?:^|\s)@(|[^\s@\n][^@\n]*)$/;

// Tags programmatic edits (setText/seed, query ops) so the trigger never opens
// the menu off them — only off a real user keystroke.
const PROGRAMMATIC_TAG = 'mention-programmatic';

/** Resolve the active `@`-query at the caret within the anchor text node. */
function $getActiveMention(): { node: TextNode; start: number; end: number; query: string } | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
  const node = selection.anchor.getNode();
  if (!$isTextNode(node)) return null;
  const end = selection.anchor.offset;
  const before = node.getTextContent().slice(0, end);
  const match = before.match(MENTION_AT_CARET);
  if (!match) return null;
  const query = match[1];
  return { node, start: end - query.length - 1, end, query };
}

/**
 * Reports the active `@`-query + caret rect. Opens the menu only when the user
 * actually types into/creates the `@query` — not when the caret merely lands on
 * a pre-existing `@` (a seeded draft, a click, or an arrow key). Once open from
 * a keystroke, it keeps tracking the query until the caret leaves it.
 */
function MentionTriggerPlugin({ onChange }: { onChange: (active: MentionQuery | null) => void }) {
  const [editor] = useLexicalComposerContext();
  const cbRef = useRef(onChange);
  cbRef.current = onChange;
  const openRef = useRef(false);
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState, dirtyLeaves, dirtyElements, tags }) => {
      const query = editorState.read(() => {
        const active = $getActiveMention();
        return active ? active.query : null;
      });
      if (query === null) {
        openRef.current = false;
        cbRef.current(null);
        return;
      }
      // A real user keystroke dirties a leaf and isn't one of our programmatic
      // edits; that — and only that — opens the menu on a fresh `@`.
      const userTyped =
        !tags.has(PROGRAMMATIC_TAG) && (dirtyLeaves.size > 0 || dirtyElements.size > 0);
      if (userTyped) openRef.current = true;
      if (!openRef.current) {
        cbRef.current(null);
        return;
      }
      // Caret rect from the live DOM selection (cleaner than mirror-div math).
      const domSel = window.getSelection();
      const rect =
        domSel && domSel.rangeCount > 0
          ? domSel.getRangeAt(0).getBoundingClientRect()
          : new DOMRect();
      cbRef.current({ query, rect });
    });
  }, [editor]);
  return null;
}

/** Capture the editor instance for the imperative handle. */
function EditorRefPlugin({ editorRef }: { editorRef: (editor: LexicalEditor) => void }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editorRef(editor);
  }, [editor, editorRef]);
  return null;
}

function AutoFocusPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.focus();
  }, [editor]);
  return null;
}

function EditablePlugin({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);
  return null;
}

/**
 * Turn pasted text into chips: reference markers (the plain-text plugin would
 * otherwise insert the raw `@[...](kind:id)`) and, where the host supports
 * them, item URLs a plugin claims — pasting a pull request link is how people
 * carry one over from GitHub, and it should reference the PR exactly as picking
 * it from the `@` menu would. A chip copied out of the transcript counts: its
 * URL is recovered from the clipboard's HTML flavor first, since the plain text
 * only carries the label. Text with none of these falls through to the default
 * paste.
 */
function PastePlugin({ contextUrls }: { contextUrls: boolean }) {
  const [editor] = useLexicalComposerContext();
  useEffect(
    () =>
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          const clipboard = event instanceof ClipboardEvent ? event.clipboardData : null;
          if (!clipboard) return false;
          const text = clipboard.getData('text/plain');
          if (!text) return false;
          // A chip copied out of the transcript carries its URL only in the
          // HTML flavor; recover it before matching so the label the user sees
          // (`#42`) references the same item its URL would.
          const html = contextUrls ? clipboard.getData('text/html') : '';
          const pieces = pastePieces(resolveLinkedContextUrls(text, html), contextUrls);
          if (!pieces) return false;
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;
            const nodes: LexicalNode[] = pieces.map((piece) =>
              piece.type === 'text'
                ? $createTextNode(piece.value)
                : $createMentionNode(piece.mention),
            );
            selection.insertNodes(nodes);
          });
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor, contextUrls],
  );
  return null;
}

/**
 * Lexical-based composer body. Text and atomic inline mention chips share one
 * editable surface, so a chip moves with the text, copy/pastes as a single
 * unit, and serializes to its marker. The host (`ChatInput`) wraps this with
 * the send button, attachments, and mention menu, and owns keyboard routing via
 * {@link MentionEditorProps.onKeyDownCapture}.
 */
export const MentionEditor = forwardRef<MentionEditorHandle, MentionEditorProps>(
  function MentionEditor(
    {
      initialText,
      placeholder,
      className,
      autoFocus,
      editable = true,
      onChange,
      onMentionQueryChange,
      onKeyDownCapture,
      onPaste,
      contextUrls = false,
    },
    ref,
  ) {
    const editorRef = useRef<LexicalEditor | null>(null);

    // `focus` returns the caret to the editor after a menu-driven op. Without
    // it, drilling into the @ menu by mouse leaves focus on the menu, so the
    // next Escape lands on the Dialog (closing the modal) instead of the
    // composer's keyboard handler.
    const run = (fn: () => void, focus = false) => {
      const editor = editorRef.current;
      if (!editor) return;
      // Tagged so the mention trigger doesn't treat a programmatic edit (seed,
      // query op) as the user typing `@`.
      editor.update(fn, { tag: PROGRAMMATIC_TAG });
      if (focus) editor.focus();
    };

    useImperativeHandle(
      ref,
      (): MentionEditorHandle => ({
        getText: () =>
          editorRef.current?.getEditorState().read(() => $getRoot().getTextContent()) ?? '',
        setText: (text) =>
          run(() => {
            const root = $getRoot();
            root.clear();
            const paragraph = $createParagraphNode();
            $appendComposerContent(paragraph, text);
            root.append(paragraph);
            paragraph.selectEnd();
          }),
        insertText: (text) =>
          run(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) selection.insertText(text);
            else {
              $getRoot().selectEnd();
              const sel = $getSelection();
              if ($isRangeSelection(sel)) sel.insertText(text);
            }
          }, true),
        insertMention: (mention) =>
          run(() => {
            let selection = $getSelection();
            if (!$isRangeSelection(selection)) {
              $getRoot().selectEnd();
              selection = $getSelection();
            }
            if (!$isRangeSelection(selection)) return;
            selection.insertNodes([$createMentionNode(mention)]);
            const after = $getSelection();
            if ($isRangeSelection(after)) after.insertText(' ');
          }, true),
        insertMentionAtQuery: (mention) =>
          run(() => {
            const active = $getActiveMention();
            if (active) active.node.spliceText(active.start, active.end - active.start, '', true);
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;
            selection.insertNodes([$createMentionNode(mention)]);
            const after = $getSelection();
            if ($isRangeSelection(after)) after.insertText(' ');
          }, true),
        openMentionMenu: () => {
          const editor = editorRef.current;
          if (!editor) return false;
          // Focus first so the inserted `@` lands at a live caret and the
          // trigger can read a real DOM selection rect for the menu.
          editor.focus();
          // Set from inside the update, which Lexical runs synchronously for a
          // top-level call like this one (the trigger is a click handler, never
          // nested in another update).
          let inserted = false;
          // NOT tagged programmatic: the mention trigger only opens the menu
          // off a real (untagged) edit that dirties a leaf, so this is the one
          // imperative op that behaves like a user keystroke.
          editor.update(() => {
            let selection = $getSelection();
            if (!$isRangeSelection(selection)) {
              $getRoot().selectEnd();
              selection = $getSelection();
            }
            if (!$isRangeSelection(selection)) return;
            const active = $getActiveMention();
            if (active) {
              // The caret already sits in an `@`-token (typed but dismissed, or
              // seeded). Reuse it rather than stacking a second `@`: dirty the
              // node so the trigger reopens the menu on the existing query,
              // leaving the text untouched.
              active.node.markDirty();
              return;
            }
            // Keep the `@` at a word boundary the trigger recognizes: add a
            // leading space when the preceding char is non-whitespace.
            const anchor = selection.anchor;
            const node = anchor.getNode();
            const before = $isTextNode(node) ? node.getTextContent().slice(0, anchor.offset) : '';
            const needsSpace = before.length > 0 && !/\s$/.test(before);
            selection.insertText(needsSpace ? ' @' : '@');
            inserted = true;
          });
          return inserted;
        },
        clearQuery: () =>
          run(() => {
            const active = $getActiveMention();
            if (active) active.node.spliceText(active.start, active.end - active.start, '', true);
          }, true),
        resetQuery: () =>
          run(() => {
            const active = $getActiveMention();
            if (active) {
              active.node.spliceText(active.start + 1, active.end - active.start - 1, '', true);
            }
          }, true),
        setQuery: (text) =>
          run(() => {
            const active = $getActiveMention();
            if (active) {
              // Replace everything after the `@` with `text`, caret at the end.
              active.node.spliceText(active.start + 1, active.end - active.start - 1, text, true);
            }
          }, true),
        clear: () =>
          run(() => {
            const root = $getRoot();
            root.clear();
            root.append($createParagraphNode());
          }),
        focus: () => editorRef.current?.focus(),
      }),
      [],
    );

    return (
      <LexicalComposer
        initialConfig={{
          namespace: 'composer',
          nodes: [MentionNode],
          theme: EDITOR_THEME,
          editorState: initialText
            ? () => {
                const root = $getRoot();
                const paragraph = $createParagraphNode();
                $appendComposerContent(paragraph, initialText);
                root.append(paragraph);
              }
            : undefined,
          onError: (error: Error) => {
            throw error;
          },
        }}
      >
        <div className="relative" onPaste={onPaste}>
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                aria-label="Message input"
                data-testid="message-input"
                spellCheck
                onKeyDownCapture={(e) => {
                  if (onKeyDownCapture?.(e)) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                className={cn(
                  'min-h-[1.5rem] w-full break-words whitespace-pre-wrap outline-none',
                  className,
                )}
              />
            }
            placeholder={
              // Carry the editor's own `className` (e.g. `text-sm`) onto the
              // placeholder so the hint matches the typed text's size and line
              // height instead of falling back to the parent's larger default.
              <div
                className={cn(
                  'pointer-events-none absolute inset-0 top-0 left-0 select-none text-muted-foreground',
                  className,
                )}
              >
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <PastePlugin contextUrls={contextUrls} />
        <EditorRefPlugin editorRef={(editor) => (editorRef.current = editor)} />
        <EditablePlugin editable={editable} />
        {onChange ? (
          <OnChangePlugin
            onChange={(state) => onChange(state.read(() => $getRoot().getTextContent()))}
          />
        ) : null}
        {onMentionQueryChange ? <MentionTriggerPlugin onChange={onMentionQueryChange} /> : null}
        {autoFocus ? <AutoFocusPlugin /> : null}
      </LexicalComposer>
    );
  },
);
